import { apiStoreNoteKeyWrapper } from '@/services/NoteKeyWrapperService';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';
import { useNoteKeyStore } from '@/store/noteKeyStore';
import {
  encryptNoteBody,
  decryptNoteBody,
  generateNoteKey,
  initSodium,
  wrapNoteKey,
} from '@/services/CryptoService';
import { resolveNoteKey } from '@/services/NoteKeyResolver';

const pendingCreateWrappers = new Map<string, { wrapped_key: string; nonce: string; alg: 'secretbox' }>();

export async function prepareNoteBody(
  noteId: string,
  body: string,
  encrypted: boolean,
  isCreating: boolean,
): Promise<{ content: string; keyWrapper?: { wrapped_key: string; nonce: string; alg: 'secretbox' } }> {
  if (!encrypted) return { content: body };

  const masterKey = useMasterPasswordStore.getState().masterKey;
  if (!masterKey) throw new Error('Unlock the master password before saving an encrypted note.');
  await initSodium();

  let wrapper: { wrapped_key: string; nonce: string; alg: 'secretbox' } | undefined;
  let noteKey = useNoteKeyStore.getState().getNoteKey(noteId);
  if (!noteKey && isCreating) {
    noteKey = generateNoteKey();
    wrapper = wrapNoteKey(noteKey, masterKey);
    pendingCreateWrappers.set(noteId, wrapper);
    useNoteKeyStore.getState().setNoteKey(noteId, noteKey);
  }
  if (isCreating) wrapper ??= pendingCreateWrappers.get(noteId);
  if (!noteKey) {
    const result = await resolveNoteKey(noteId);
    if (!result.ok) throw new Error(`Could not unlock this note key (${result.reason}).`);
    noteKey = result.noteKey;
  }

  return { content: encryptNoteBody(body, noteId, noteKey), keyWrapper: wrapper };
}

export async function openNoteBody(noteId: string, envelope: string): Promise<string> {
  const result = await resolveNoteKey(noteId);
  if (!result.ok) throw new Error(`Could not unlock this note key (${result.reason}).`);
  await initSodium();
  return decryptNoteBody(envelope, noteId, result.noteKey);
}

export async function ensureNoteKeyWrapper(noteId: string): Promise<void> {
  const store = useNoteKeyStore.getState();
  if (store.getNoteKey(noteId)) return;
  const masterKey = useMasterPasswordStore.getState().masterKey;
  if (!masterKey) throw new Error('Unlock your master password before changing encryption.');

  const resolved = await resolveNoteKey(noteId);
  if (resolved.ok) return;
  if (resolved.reason !== 'no-wrapper') throw new Error('Could not unlock the note key. Check your master password and retry.');

  await initSodium();
  const noteKey = generateNoteKey();
  const wrapper = wrapNoteKey(noteKey, masterKey);
  try {
    await apiStoreNoteKeyWrapper(noteId, wrapper);
    store.setNoteKey(noteId, noteKey);
  } catch (error: any) {
    // A concurrent tab may have created the wrapper. Resolve that canonical key.
    if (error?.status !== 409) {
      useNoteKeyStore.getState().clearNoteKey(noteId);
      throw error;
    }
    const resolvedAgain = await resolveNoteKey(noteId);
    if (!resolvedAgain.ok) throw new Error('Could not load the existing note key. Refresh and retry.');
    useNoteKeyStore.getState().clearNoteKey(noteId);
    useNoteKeyStore.getState().setNoteKey(noteId, resolvedAgain.noteKey);
  }
}
